import React, { useState, useEffect, useRef, useCallback } from 'react'
import './AnalyzeChat.css'
import ReactMarkdown from 'react-markdown'

// Mapping of backend badge.type to color + default label
const BADGE_TYPE_MAP = {
  agent: { color: 'mint' },
  rag: { color: 'violet' },
  file: { color: 'blue' },
  files: { color: 'blue' } // fallback plural if used
}

const endpoints = {
  'bot.dev.stratsys.net': 'https://data-ai-api-backend.svc.dev.stratsys.net',
  'bot.test.stratsys.net': 'https://data-ai-api-backend.svc.test.stratsys.net',
  'bot.stratsys.com': 'https://data-ai-api-backend.svc.stratsys.com',
  'localhost:8080': 'http://localhost:5000'
}

const baseUrl = endpoints[window.location.host] || 'http://localhost:5000'

// Available products for the dropdown
const products = [
  'OperationalPlanning',
  'BuildYourOwn',
  'Sustainability',
  'RiskAndControl',
  'InformationSecurity',
  'HealthSafety',
  'SOSFS',
  'PortfolioManagement',
  'qualitymanagementforschools'
]

const AnalyzeChat = () => {
  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedDrafts, setSelectedDrafts] = useState({})
  const [language, setLanguage] = useState('en')
  const [inputProduct, setInputProduct] = useState('Sustainability')
  // Department (new field to send with POST conversation body)
  const [department, setDepartment] = useState('')
  // New state inputs for location and context (JSON)
  const [location, setLocation] = useState('view')
  const [contextText, setContextText] = useState(() => JSON.stringify({ currently_open: 'Ekonomi' }, null, 2))
  const [contextIsValid, setContextIsValid] = useState(true)
  const [contextError, setContextError] = useState('')
  // Intentions (auto-suggest) state
  const [intentions, setIntentions] = useState([]) // array of strings
  const [intentionsLoading, setIntentionsLoading] = useState(false)
  // New richer objects: [{ type, text }]
  const [intentionObjects, setIntentionObjects] = useState([])
  // New: backend returned response intentions (plan proposal objects) to choose from
  const [responseIntentions, setResponseIntentions] = useState([]) // [{ question, answer, plan: [{text,value}, ...] }]
  // Flag: when true we are in a follow-up request triggered by selecting an intention
  const intentionFollowUpRef = useRef(false)
  // Track which intentions user has clicked (toggled on). Sent with POST conversation requests.
  const [clickedIntentions, setClickedIntentions] = useState([]) // array of strings
  const lastIntentionsTriggerRef = useRef(0) // word count last fetched
  const intentionsAbortRef = useRef(null)
  // Track last query we issued due to a pause-trigger to avoid duplicates
  const lastPauseIntentionsQueryRef = useRef('')

  // Streaming-specific state
  const [streamingEnabled, setStreamingEnabled] = useState(true)
  const [cancelToken, setCancelToken] = useState(null)

  // File upload state (query removed – uploads stand alone)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  // Single unified status text instead of separate success/error elements
  const [fileStatus, setFileStatus] = useState('')
  const [fileStatusIsError, setFileStatusIsError] = useState(false)
  const [fileStatusIsSuccess, setFileStatusIsSuccess] = useState(false)
  const MAX_SIZE_BYTES = 128 * 1024 * 1024 // 128MB (mirror backend)

  // Live update bubble (ephemeral). Now stores an object: { text, icon, badge }
  const [updateMessage, setUpdateMessage] = useState(null)
  // Keep a list of all update_message objects for the *current* stream (chronological)
  const [updateHistory, setUpdateHistory] = useState([]) // each entry: { text, icon?, badge? }

  // Buffer for incremental stream parsing (SSE or raw JSON objects)
  const streamBufferRef = useRef('')

  // Approval flow state
  const [pendingApproval, setPendingApproval] = useState(null) // { sessionId, items, decisions }
  const [editingItemId, setEditingItemId] = useState(null)
  const [editingItemName, setEditingItemName] = useState('')
  const [approvalSubmitting, setApprovalSubmitting] = useState(false)

  // Initialize approval state with all items approved by default
  const initializeApprovalState = useCallback((pending) => {
    const decisions = new Map()
    pending.items.forEach((item) => {
      decisions.set(item.id, { approved: true })
    })
    return {
      sessionId: pending.session_id,
      items: pending.items,
      decisions
    }
  }, [])

  const fetchChatHistory = useCallback(async () => {
    try {
      // Build query params for product & location (new requirement)
      const qs = new URLSearchParams()
      if (inputProduct) qs.append('product', inputProduct)
      if (location) qs.append('location', location)
      const url = `${baseUrl}/conversation${qs.toString() ? `?${qs.toString()}` : ''}`
      const response = await window.PDR.api.get(url)
      const data = response
      if (data.chat_history) {
        setMessages(data.chat_history)
      }
      // Check for pending_approval in the GET response
      if (data.pending_approval && data.pending_approval.items && data.pending_approval.items.length > 0) {
        // Extract session_id from the last chat_history message if available
        let sessionId = null
        if (data.chat_history && data.chat_history.length > 0) {
          const lastMsg = data.chat_history[data.chat_history.length - 1]
          sessionId = lastMsg.session_id || null
        }
        // Fallback to localStorage if no session_id found
        if (!sessionId) {
          sessionId = localStorage.getItem('ac_pending_session_id')
        }
        if (sessionId) {
          const state = initializeApprovalState({
            session_id: sessionId,
            items: data.pending_approval.items
          })
          setPendingApproval(state)
          localStorage.setItem('ac_pending_session_id', sessionId)
        }
      }
    } catch (error) {
      console.error('Error fetching chat history:', error)
    }
  }, [inputProduct, location, initializeApprovalState])

  useEffect(() => {
    fetchChatHistory()
  }, [fetchChatHistory])

  // --- Utilities -----------------------------------------------------------

  // Extract as many complete JSON objects as possible from the buffer.
  // Supports plain lines like `{...}` and SSE lines like `data: {...}` separated by blank lines.
  function drainCompleteJsonFromBuffer() {
    const objs = []
    let buf = streamBufferRef.current

    // 1) First, greedily process Server-Sent Events blocks separated by double newlines
    while (true) {
      const sep = buf.indexOf('\n\n')
      if (sep === -1) break
      const evt = buf.slice(0, sep)
      buf = buf.slice(sep + 2)
      const dataLines = evt
        .split(/\n/)
        .map((l) => l.trim())
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.replace(/^data:/, '').trim())
      if (dataLines.length) {
        const payload = dataLines.join('\n')
        try {
          objs.push(JSON.parse(payload))
        } catch (_) {
          // If it wasn't complete JSON, push back into buffer tail to try again later
          buf = payload + '\n\n' + buf
          break
        }
      }
    }

    // 2) Next, try to peel off top-level balanced JSON objects from the start of the buffer
    //    This handles streams that just send `{...}\n{...}` without SSE framing.
    let i = 0
    // Skip non-brace noise at the beginning (e.g., stray newlines)
    while (i < buf.length && buf[i] !== '{') i++
    if (i > 0) buf = buf.slice(i)

    const start = 0
    let depth = 0
    let inStr = false
    let esc = false
    let endIdx = -1

    for (let j = start; j < buf.length; j++) {
      const ch = buf[j]
      if (inStr) {
        if (esc) {
          esc = false
        } else if (ch === '\\') {
          esc = true
        } else if (ch === '"') {
          inStr = false
        }
      } else {
        if (ch === '"') inStr = true
        else if (ch === '{') depth++
        else if (ch === '}') {
          depth--
          if (depth === 0) {
            endIdx = j
            break
          }
        }
      }
    }

    if (endIdx !== -1) {
      const jsonSlice = buf.slice(0, endIdx + 1)
      const rest = buf.slice(endIdx + 1)
      try {
        const obj = JSON.parse(jsonSlice)
        objs.push(obj)
        buf = rest
        // There might be more JSON glued after this one; recurse once
        streamBufferRef.current = buf
        return objs.concat(drainCompleteJsonFromBuffer())
      } catch (_) {
        // fall through; leave in buffer
      }
    }

    streamBufferRef.current = buf
    return objs
  }

  // Check for pending approval on page load
  const checkPendingApproval = useCallback(async () => {
    try {
      // Try to get session_id from localStorage or a ref if stored
      const storedSessionId = localStorage.getItem('ac_pending_session_id')
      if (!storedSessionId) return
      const response = await window.PDR.api.get(`${baseUrl}/conversation/${storedSessionId}/pending-approval`)
      if (response && response.has_pending && response.pending_approval) {
        const state = initializeApprovalState({
          session_id: storedSessionId,
          items: response.pending_approval.items
        })
        setPendingApproval(state)
      } else {
        localStorage.removeItem('ac_pending_session_id')
      }
    } catch (e) {
      console.warn('Failed to check pending approval:', e)
      localStorage.removeItem('ac_pending_session_id')
    }
  }, [initializeApprovalState])

  useEffect(() => {
    checkPendingApproval()
  }, [checkPendingApproval])

  const ingestEventObject = (obj, tempId) => {
    // Update language if present
    if (obj.language) setLanguage(obj.language)

    // Check for approval_required status
    if (obj.status === 'approval_required' && obj.pending_approval) {
      const state = initializeApprovalState(obj.pending_approval)
      setPendingApproval(state)
      // Store session_id for page refresh recovery
      localStorage.setItem('ac_pending_session_id', obj.pending_approval.session_id)
      // Hide loading state since we're waiting for user input
      setLoading(false)
      setCancelToken(null)
      // Remove the placeholder AI message since we show approval UI instead
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
      setUpdateMessage(null)
      setUpdateHistory([])
      return // Don't process further
    }

    // Capture responseIntentions if present in a streamed chunk; suppress output accumulation
    if (!intentionFollowUpRef.current && Array.isArray(obj.intentions) && obj.intentions.length > 0) {
      // Shape: { question, answer, plan: [] }. Backward compat: allow .text.
      const normalized = obj.intentions
        .filter(
          (it) => it && typeof it === 'object' && (typeof it.question === 'string' || typeof it.text === 'string')
        )
        .slice(0, 2)
        .map((it) => ({
          question: it.question || it.text,
          answer: typeof it.answer === 'string' ? it.answer : it.text || it.question || '',
          plan: Array.isArray(it.plan) ? it.plan : []
        }))
      if (normalized.length) setResponseIntentions(normalized)
    }

    // 1) update_message => show/refresh the animated live bubble (ephemeral) + keep history
    //    Now supports object: { text: string, icon?: string, badge?: string }
    if (obj.update_message) {
      let updateObj = null
      if (typeof obj.update_message === 'string') {
        const trimmed = obj.update_message.trim()
        if (trimmed.length > 0) updateObj = { text: trimmed }
      } else if (typeof obj.update_message === 'object') {
        // Expect shape { text, icon?: string|{name,set}, badge? }
        const txt = typeof obj.update_message.text === 'string' ? obj.update_message.text.trim() : ''
        if (txt.length > 0) {
          let iconVal = obj.update_message.icon
          if (iconVal && typeof iconVal === 'object') {
            iconVal = {
              name: iconVal.name || '',
              set: iconVal.set || ''
            }
          } else if (typeof iconVal === 'string') {
            // legacy simple string icon
            iconVal = { name: iconVal, set: '' }
          } else {
            iconVal = undefined
          }
          // Normalize badge: backend may send badge object like { type, text, icon }
          let badgeVal = obj.update_message.badge
          if (badgeVal) {
            if (typeof badgeVal === 'string') {
              badgeVal = { type: badgeVal }
            } else if (typeof badgeVal === 'object') {
              badgeVal = {
                type: badgeVal.type || badgeVal.name || '',
                text: typeof badgeVal.text === 'string' ? badgeVal.text : undefined,
                // optional nested icon for badge
                icon:
                  badgeVal.icon && typeof badgeVal.icon === 'object'
                    ? { name: badgeVal.icon.name || '', set: badgeVal.icon.set || '' }
                    : undefined
              }
            }
            if (badgeVal.type) {
              const map = BADGE_TYPE_MAP[badgeVal.type]
              if (map) {
                badgeVal = {
                  type: badgeVal.type,
                  color: map.color,
                  text: badgeVal.text,
                  icon: badgeVal.icon
                }
              }
            }
          }
          updateObj = { text: txt, icon: iconVal, badge: badgeVal || undefined }
        }
      }

      if (updateObj) {
        setUpdateMessage(updateObj)
        setUpdateHistory((prev) => {
          if (!prev.length) return [updateObj]
          // Avoid duplicates by text+badge+icon signature
          const last = prev[prev.length - 1]
          const lastIconName = last.icon && typeof last.icon === 'object' ? last.icon.name : last.icon || ''
          const lastIconSet = last.icon && typeof last.icon === 'object' ? last.icon.set : ''
          let newIconName
          if (updateObj.icon && typeof updateObj.icon === 'object') {
            newIconName = updateObj.icon.name
          } else {
            newIconName = updateObj.icon || ''
          }
          const newIconSet = updateObj.icon && typeof updateObj.icon === 'object' ? updateObj.icon.set : ''
          if (
            last.text === updateObj.text &&
            lastIconName === newIconName &&
            lastIconSet === newIconSet &&
            (last.badge && last.badge.type ? last.badge.type : last.badge || '') ===
              (updateObj.badge && updateObj.badge.type ? updateObj.badge.type : updateObj.badge || '')
          ) {
            return prev
          }
          return [...prev, updateObj]
        })
      }
    }

    // 2) output => append to the placeholder AI message (but ignore "[Done]")
    // Allow output streaming if either no intentions are active OR we are in a follow-up (selected intention)
    if (typeof obj.output === 'string' && (responseIntentions.length === 0 || intentionFollowUpRef.current)) {
      const trimmed = obj.output.trim()
      if (trimmed === '[Done]') {
        // Hide the live update bubble and clear its history when we get the done signal
        setUpdateMessage(null)
        setUpdateHistory([])
      } else {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, content: (m.content ? m.content + '\n' : '') + obj.output } : m))
        )
      }
    }
  }

  // Handle sending a new question
  const handleAsk = async () => {
    if (!inputText.trim() || loading) return

    // Validate / parse context JSON before sending
    let parsedContext = {}
    try {
      parsedContext = contextText.trim() ? JSON.parse(contextText) : {}
      setContextIsValid(true)
      setContextError('')
    } catch (e) {
      setContextIsValid(false)
      setContextError('Context must be valid JSON.')
      return
    }

    // Clear any pending approval when user sends a new message
    if (pendingApproval) {
      setPendingApproval(null)
      localStorage.removeItem('ac_pending_session_id')
    }

    // Append the human message immediately
    const humanMessage = {
      id: Date.now().toString(),
      role: 'human',
      content: inputText
    }
    setMessages((prev) => [...prev, humanMessage])

    // Discard any existing responseIntentions when user sends a free-text query
    if (responseIntentions.length) setResponseIntentions([])

    setLoading(true)

    if (!streamingEnabled) {
      // --- Non-streaming flow (unchanged) ---
      try {
        const state = {}
        if (location.trim()) state.location = location.trim()
        if (Object.keys(parsedContext).length > 0) state.context = parsedContext
        const payload = {
          query: inputText,
          product: inputProduct,
          ...(department.trim() ? { department: department.trim() } : {}),
          ...(clickedIntentions.length ? { intentions: clickedIntentions } : {}),
          ...(Object.keys(state).length > 0 ? { state } : {})
        }
        const response = await window.PDR.api
          .withScopes('stratsys.scorecard.readwrite')
          .post(`${baseUrl}/conversation`, payload)
        const result = response
        setLanguage(result.language)

        // If backend returns intentions proposals, show those instead of output bubble
        if (Array.isArray(result.intentions) && result.intentions.length > 0) {
          const normalized = result.intentions
            .filter(
              (it) => it && typeof it === 'object' && (typeof it.question === 'string' || typeof it.text === 'string')
            )
            .slice(0, 2)
            .map((it) => ({
              question: it.question || it.text,
              answer: typeof it.answer === 'string' ? it.answer : it.text || it.question || '',
              plan: Array.isArray(it.plan) ? it.plan : []
            }))
          setResponseIntentions(normalized)
          setLoading(false)
          setInputText('')
          return
        }

        const draftPayload = Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []

        const botMessage = {
          id: result.id,
          role: 'ai',
          content: result.output,
          draft: draftPayload
        }

        setMessages((prev) => [...prev, botMessage])
      } catch (error) {
        console.error('Error sending query:', error)
      } finally {
        setLoading(false)
        setInputText('')
      }
      return
    }

    // --- Streaming flow ---
    const tempId = `temp-${Date.now()}`
    // Create a placeholder AI message that we'll fill as JSON chunks arrive
    setMessages((prev) => [...prev, { id: tempId, role: 'ai', content: '', draft: [] }])

    // Reset live update bubble and buffer/history
    setUpdateMessage(null)
    setUpdateHistory([])
    streamBufferRef.current = ''

    // Prepare cancellation token and stream handler
    const token = window.PDR.api.createCancellationToken()
    setCancelToken(token)

    const streamOptions = {
      cancellationToken: token,
      options: {
        stream: (partial) => {
          if (typeof partial !== 'string' || partial.length === 0) return
          // Accumulate and parse robustly
          streamBufferRef.current += partial
          const objs = drainCompleteJsonFromBuffer()
          if (objs && objs.length) {
            for (const obj of objs) {
              if (obj && typeof obj === 'object') {
                ingestEventObject(obj, tempId)
              }
            }
          }
        }
      }
    }

    try {
      const state = {}
      if (location.trim()) state.location = location.trim()
      if (Object.keys(parsedContext).length > 0) state.context = parsedContext
      const payload = {
        query: inputText,
        product: inputProduct,
        ...(department.trim() ? { department: department.trim() } : {}),
        ...(Object.keys(state).length > 0 ? { state } : {}),
        ...(clickedIntentions.length ? { intentions: clickedIntentions } : {}),
        // Requirement #1: include stream flag in POST body
        stream: true
      }

      // Post with stream handler; final JSON arrives when stream completes
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload, streamOptions)
      const result = response

      // Persist language if provided
      if (result && result.language) setLanguage(result.language)

      const finalDrafts = Array.isArray(result?.draft) ? result.draft : result?.draft ? [result.draft] : []

      // If intentions present, suppress final AI bubble content and show intention cards
      if (Array.isArray(result?.intentions) && result.intentions.length > 0) {
        const normalized = result.intentions
          .filter(
            (it) => it && typeof it === 'object' && (typeof it.question === 'string' || typeof it.text === 'string')
          )
          .slice(0, 2)
          .map((it) => ({
            question: it.question || it.text,
            answer: typeof it.answer === 'string' ? it.answer : it.text || it.question || '',
            plan: Array.isArray(it.plan) ? it.plan : []
          }))
        setResponseIntentions(normalized)
        // Remove placeholder AI message since we're not showing output
        setMessages((prev) => prev.filter((m) => m.id !== tempId))
      } else {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== tempId) return m
            const finalText =
              typeof result?.output === 'string' && result.output.trim() !== '[Done]' ? result.output : m.content
            return {
              id: result?.id || tempId,
              role: 'ai',
              content: finalText,
              draft: finalDrafts
            }
          })
        )
      }

      // Done: hide the live update bubble and clear history
      setUpdateMessage(null)
      setUpdateHistory([])
    } catch (error) {
      console.error('Error during streaming:', error)
      // Mark the placeholder as errored
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, content: (m.content || '') + '\n\n*An error occurred while streaming.*' } : m
        )
      )
    } finally {
      setLoading(false)
      setCancelToken(null)
      setInputText('')
      // Clear any residual buffer
      streamBufferRef.current = ''
    }
  }

  // Handle clicking a backend response intention proposal
  const handleSendResponseIntention = async (intentionObj) => {
    if (!intentionObj || loading) return
    const queryText = intentionObj.answer || intentionObj.question || ''
    // Mark follow-up mode so we render answer output even if backend sends intentions again
    intentionFollowUpRef.current = true
    // Visualize user sending the ANSWER
    const humanMessage = {
      id: `intent-${Date.now()}`,
      role: 'human',
      content: queryText
    }
    setMessages((prev) => [...prev, humanMessage])
    // Discard cards immediately
    setResponseIntentions([])
    setLoading(true)

    // Prepare optional state context like handleAsk
    let parsedContext = {}
    try {
      parsedContext = contextText.trim() ? JSON.parse(contextText) : {}
    } catch (_) {
      parsedContext = {}
    }
    const state = {}
    if (location.trim()) state.location = location.trim()
    if (Object.keys(parsedContext).length > 0) state.context = parsedContext

    // Allow streaming same as normal ask
    const payloadBase = {
      query: queryText,
      intention: intentionObj, // send full object as well
      product: inputProduct,
      ...(department.trim() ? { department: department.trim() } : {}),
      ...(Object.keys(state).length > 0 ? { state } : {})
    }

    if (!streamingEnabled) {
      try {
        const response = await window.PDR.api.post(`${baseUrl}/conversation`, payloadBase)
        const result = response
        setLanguage(result.language)
        // For follow-up we always show output; ignore any returned intentions here
        if (!(Array.isArray(result.intentions) && result.intentions.length > 0)) {
          const draftPayload = Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
          setMessages((prev) => [...prev, { id: result.id, role: 'ai', content: result.output, draft: draftPayload }])
        }
      } catch (e) {
        console.error('Error sending intention:', e)
      } finally {
        setLoading(false)
      }
      return
    }

    // Streaming flow for intention selection
    const tempId = `temp-${Date.now()}`
    setMessages((prev) => [...prev, { id: tempId, role: 'ai', content: '', draft: [] }])
    setUpdateMessage(null)
    setUpdateHistory([])
    streamBufferRef.current = ''
    const token = window.PDR.api.createCancellationToken()
    setCancelToken(token)
    const streamOptions = {
      cancellationToken: token,
      options: {
        stream: (partial) => {
          if (typeof partial !== 'string' || partial.length === 0) return
          streamBufferRef.current += partial
          const objs = drainCompleteJsonFromBuffer()
          if (objs && objs.length) {
            for (const obj of objs) {
              if (obj && typeof obj === 'object') {
                ingestEventObject(obj, tempId)
              }
            }
          }
        }
      }
    }
    try {
      const response = await window.PDR.api.post(
        `${baseUrl}/conversation`,
        { ...payloadBase, stream: true },
        streamOptions
      )
      const result = response
      if (result && result.language) setLanguage(result.language)
      const finalDrafts = Array.isArray(result?.draft) ? result.draft : result?.draft ? [result.draft] : []
      if (!(Array.isArray(result?.intentions) && result.intentions.length > 0)) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== tempId) return m
            const finalText =
              typeof result?.output === 'string' && result.output.trim() !== '[Done]' ? result.output : m.content
            return {
              id: result?.id || tempId,
              role: 'ai',
              content: finalText,
              draft: finalDrafts
            }
          })
        )
      }
      setUpdateMessage(null)
      setUpdateHistory([])
    } catch (e) {
      console.error('Error streaming intention:', e)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, content: (m.content || '') + '\n\n*An error occurred while streaming.*' } : m
        )
      )
    } finally {
      setLoading(false)
      setCancelToken(null)
      streamBufferRef.current = ''
      intentionFollowUpRef.current = false
    }
  }

  const handleCancelStream = () => {
    try {
      if (cancelToken) {
        cancelToken.cancel()
      }
    } catch (e) {
      console.warn('Cancel failed or token missing', e)
    } finally {
      setLoading(false)
      setCancelToken(null)
      setUpdateMessage(null)
      setUpdateHistory([])
      // leave any partial bot content as-is
    }
  }

  // --- Approval Flow Handlers -----------------------------------------------

  const toggleApprovalItem = (itemId) => {
    if (!pendingApproval) return
    setPendingApproval((prev) => {
      const newDecisions = new Map(prev.decisions)
      const current = newDecisions.get(itemId) || { approved: true }
      newDecisions.set(itemId, { ...current, approved: !current.approved })
      return { ...prev, decisions: newDecisions }
    })
  }

  const startEditingItem = (item) => {
    setEditingItemId(item.id)
    const decision = pendingApproval?.decisions.get(item.id)
    setEditingItemName(decision?.editedName || item.name)
  }

  const confirmEditItem = () => {
    if (!pendingApproval || !editingItemId) return
    setPendingApproval((prev) => {
      const newDecisions = new Map(prev.decisions)
      const current = newDecisions.get(editingItemId) || { approved: true }
      newDecisions.set(editingItemId, { ...current, editedName: editingItemName })
      return { ...prev, decisions: newDecisions }
    })
    setEditingItemId(null)
    setEditingItemName('')
  }

  const cancelEditItem = () => {
    setEditingItemId(null)
    setEditingItemName('')
  }

  const handleCancelAllApprovals = async () => {
    if (!pendingApproval) return
    // Set all items to rejected
    const decisions = pendingApproval.items.map((item) => ({
      id: item.id,
      approved: false
    }))
    await submitApprovalDecisions(decisions)
  }

  const handleConfirmSelectedApprovals = async () => {
    if (!pendingApproval) return
    const decisions = pendingApproval.items.map((item) => {
      const decision = pendingApproval.decisions.get(item.id)
      return {
        id: item.id,
        approved: decision?.approved ?? false,
        ...(decision?.editedName && { edited_name: decision.editedName })
      }
    })
    await submitApprovalDecisions(decisions)
  }

  const submitApprovalDecisions = async (decisions) => {
    if (!pendingApproval || approvalSubmitting) return
    setApprovalSubmitting(true)
    setLoading(true)

    // Create a placeholder AI message for the response
    const tempId = `temp-approval-${Date.now()}`
    setMessages((prev) => [...prev, { id: tempId, role: 'ai', content: '', draft: [] }])
    setUpdateMessage(null)
    setUpdateHistory([])
    streamBufferRef.current = ''

    const payload = {
      session_id: pendingApproval.sessionId,
      resume_approval: true,
      stream: streamingEnabled,
      decisions
    }

    // Clear pending approval state
    setPendingApproval(null)
    localStorage.removeItem('ac_pending_session_id')

    if (!streamingEnabled) {
      try {
        const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload)
        const result = response
        if (result.language) setLanguage(result.language)
        const draftPayload = Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId ? { id: result.id || tempId, role: 'ai', content: result.output, draft: draftPayload } : m
          )
        )
      } catch (e) {
        console.error('Error submitting approval:', e)
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, content: '*An error occurred while processing approval.*' } : m))
        )
      } finally {
        setLoading(false)
        setApprovalSubmitting(false)
      }
      return
    }

    // Streaming flow
    const token = window.PDR.api.createCancellationToken()
    setCancelToken(token)
    const streamOptions = {
      cancellationToken: token,
      options: {
        stream: (partial) => {
          if (typeof partial !== 'string' || partial.length === 0) return
          streamBufferRef.current += partial
          const objs = drainCompleteJsonFromBuffer()
          if (objs && objs.length) {
            for (const obj of objs) {
              if (obj && typeof obj === 'object') {
                ingestEventObject(obj, tempId)
              }
            }
          }
        }
      }
    }

    try {
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload, streamOptions)
      const result = response
      if (result && result.language) setLanguage(result.language)
      const finalDrafts = Array.isArray(result?.draft) ? result.draft : result?.draft ? [result.draft] : []
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== tempId) return m
          const finalText =
            typeof result?.output === 'string' && result.output.trim() !== '[Done]' ? result.output : m.content
          return {
            id: result?.id || tempId,
            role: 'ai',
            content: finalText,
            draft: finalDrafts
          }
        })
      )
      setUpdateMessage(null)
      setUpdateHistory([])
    } catch (e) {
      console.error('Error streaming approval response:', e)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, content: (m.content || '') + '\n\n*An error occurred while streaming.*' } : m
        )
      )
    } finally {
      setLoading(false)
      setCancelToken(null)
      setApprovalSubmitting(false)
      streamBufferRef.current = ''
    }
  }

  // --- Intentions fetching -------------------------------------------------
  // Assumptions:
  //  - Backend supports GET /conversation?query=... returning an 'intentions' array when query present.
  //  - If this differs, adjust `fetchIntentions` endpoint/response parsing accordingly.
  // Triggers after 4 words, then every +3 words (4,7,10,...). Debounced.

  const fetchIntentions = useCallback(
    async (query) => {
      if (!query || query.trim().length === 0) return
      // Abort any in-flight intentions request
      try {
        if (intentionsAbortRef.current) intentionsAbortRef.current.abort()
      } catch (_) {}
      const controller = new AbortController()
      intentionsAbortRef.current = controller
      setIntentionsLoading(true)
      try {
        const qs = new URLSearchParams()
        qs.append('query', query)
        if (inputProduct) qs.append('product', inputProduct)
        const url = `${baseUrl}/intentions?${qs.toString()}`
        const response = await window.PDR.api.get(url, { signal: controller.signal })
        // Try a few shapes for intentions
        const intents =
          (response && (response.intentions || response.Intentions)) ||
          (response && response.data && (response.data.intentions || response.data.Intentions)) ||
          []
        const objects =
          (response && (response.objects || response.Objects)) ||
          (response && response.data && (response.data.objects || response.data.Objects)) ||
          []

        // Normalize objects to { type, text }
        let normalizedObjects = []
        if (Array.isArray(objects)) {
          normalizedObjects = objects
            .map((o) => {
              if (!o) return null
              if (typeof o === 'string') return null
              const type = o.type || o.name || o.id
              const text = o.text || o.label || o.display || type
              if (!type || !text) return null
              return { type: String(type), text: String(text) }
            })
            .filter(Boolean)
        }

        if (normalizedObjects.length > 0) {
          setIntentionObjects(normalizedObjects)
          // Fallback plain intentions not needed visually when objects present
          setIntentions([])
        } else if (Array.isArray(intents)) {
          setIntentionObjects([])
          setIntentions(intents.map((i) => (typeof i === 'string' ? i : i.text || JSON.stringify(i))))
        } else {
          setIntentionObjects([])
          setIntentions([])
        }
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.warn('Failed to fetch intentions', e)
          setIntentions([])
          setIntentionObjects([])
        }
      } finally {
        setIntentionsLoading(false)
      }
    },
    [inputProduct]
  )

  useEffect(() => {
    // Debounce word-based trigger
    const handler = setTimeout(() => {
      const words = inputText
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0)
      const count = words.length
      if (count < 4) return
      const shouldTrigger = count === 4 || ((count - 4) % 2 === 0 && count !== lastIntentionsTriggerRef.current)
      if (shouldTrigger && count !== lastIntentionsTriggerRef.current) {
        lastIntentionsTriggerRef.current = count
        fetchIntentions(inputText.trim())
      }
    }, 350) // 350ms debounce
    return () => clearTimeout(handler)
  }, [inputText, fetchIntentions])

  // Pause-based (1s inactivity) trigger: if user stops typing for 1s, fetch intentions even if word thresholds not met.
  useEffect(() => {
    if (!inputText.trim()) return
    const timer = setTimeout(() => {
      const query = inputText.trim()
      if (!query) return
      // Avoid duplicate if we already fetched via pause for identical query
      if (lastPauseIntentionsQueryRef.current === query) return
      // Also avoid if the last word-based trigger already handled this exact query state
      lastPauseIntentionsQueryRef.current = query
      fetchIntentions(query)
    }, 1000) // 1 second pause
    return () => clearTimeout(timer)
  }, [inputText, fetchIntentions])

  // Clear intentions when query cleared
  useEffect(() => {
    if (inputText.trim().length === 0) {
      setIntentions([])
      setIntentionObjects([])
      setClickedIntentions([])
      lastIntentionsTriggerRef.current = 0
      lastPauseIntentionsQueryRef.current = ''
      try {
        if (intentionsAbortRef.current) intentionsAbortRef.current.abort()
      } catch (_) {}
    }
  }, [inputText])

  // Toggle selection of an intention bubble (for sending with conversation request)
  const toggleClickedIntention = (intent) => {
    setClickedIntentions((prev) => (prev.includes(intent) ? prev.filter((i) => i !== intent) : [...prev, intent]))
  }

  const allowedExtensions = ['txt', 'pdf', 'docx', 'md', 'ppt', 'pptx']
  const fileIsAllowed = (name) => {
    if (!name || !name.includes('.')) return false
    const ext = name.split('.').pop().toLowerCase()
    return allowedExtensions.includes(ext)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null
    setFileStatus('')
    setFileStatusIsError(false)
    setFileStatusIsSuccess(false)
    if (!file) {
      setSelectedFile(null)
      return
    }
    if (!fileIsAllowed(file.name)) {
      setFileStatus(`File type not allowed. Allowed: ${allowedExtensions.join(', ')}`)
      setFileStatusIsError(true)
      setFileStatusIsSuccess(false)
      setSelectedFile(null)
      return
    }
    if (file.size > MAX_SIZE_BYTES) {
      setFileStatus('File exceeds max size of 128MB')
      setFileStatusIsError(true)
      setFileStatusIsSuccess(false)
      setSelectedFile(null)
      return
    }
    setSelectedFile(file)
    setFileStatus(file.name)
    setFileStatusIsError(false)
    setFileStatusIsSuccess(false)
  }

  const handleFileUpload = async () => {
    setFileStatusIsError(false)
    setFileStatusIsSuccess(false)
    if (uploading) return
    if (!selectedFile) {
      setFileStatus('Choose a file first')
      setFileStatusIsError(true)
      setFileStatusIsSuccess(false)
      return
    }
    const formData = new FormData()
    formData.append('file', selectedFile)
    // Intentionally no query parameter anymore
    setUploading(true)
    try {
      let resp
      try {
        resp = await window.PDR.api.post(`${baseUrl}/upload_file`, formData)
      } catch (inner) {
        resp = await fetch(`${baseUrl}/upload_file`, { method: 'POST', body: formData })
        if (resp && resp.json) resp = await resp.json()
      }
      const result = resp
      if (result && result.message === 'File uploaded') {
        setFileStatus(`${result.file}`)
        setFileStatusIsError(false)
        setFileStatusIsSuccess(true)
        setMessages((prev) => [
          ...prev,
          {
            id: `file-${Date.now()}`,
            role: 'ai',
            content: `**File uploaded:** ${result.file} (size ${Math.round(result.size_bytes / 1024)} KB)\nOpenAI ID: ${result.openai_file?.id || 'N/A'}`,
            draft: []
          }
        ])
        setSelectedFile(null)
      } else {
        setFileStatus(result && result.message ? result.message : 'Upload failed')
        setFileStatusIsError(true)
        setFileStatusIsSuccess(false)
      }
    } catch (e) {
      console.error('Upload error:', e)
      setFileStatus('Upload failed')
      setFileStatusIsError(true)
      setFileStatusIsSuccess(false)
    } finally {
      setUploading(false)
    }
  }

  // Single draft accept: wrap the draft in an array with Status "Accepted"
  const handleDraftAccept = async (msgId, draftItem) => {
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, draft: [], showDraftLoading: true } : m)))
    const acceptedDraft = { ...draftItem, Status: 'Accepted' }
    try {
      const payload = { query: '', draft: [acceptedDraft], language: language }
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload)
      const result = response
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, showDraftLoading: false } : m)))
      const botMessage = {
        id: result.id,
        role: 'ai',
        content: result.output,
        draft: Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
      }
      setMessages((prev) => [...prev, botMessage])
    } catch (error) {
      console.error('Error accepting draft:', error)
    }
  }

  // Single draft rejection: wrap the draft in an array with Status "Rejected"
  const handleDraftReject = async (msgId) => {
    const message = messages.find((m) => m.id === msgId)
    if (!message || !message.draft || message.draft.length === 0) return
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, draft: [], showDraftLoading: true } : m)))
    const rejectedDrafts = message.draft.map((draftItem) => ({
      ...draftItem,
      Status: 'Rejected'
    }))
    try {
      const payload = { query: '', draft: rejectedDrafts, language: language }
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload)
      const result = response
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, showDraftLoading: false } : m)))
      const botMessage = {
        id: result.id,
        role: 'ai',
        content: result.output,
        draft: Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
      }
      setMessages((prev) => [...prev, botMessage])
    } catch (error) {
      console.error('Error rejecting draft:', error)
    }
  }

  // Handler for checkbox change in multiple drafts
  const handleCheckboxChange = (msgId, index, checked) => {
    setSelectedDrafts((prev) => {
      const currentSelections = prev[msgId] ? [...prev[msgId]] : []
      currentSelections[index] = checked
      return { ...prev, [msgId]: currentSelections }
    })
  }

  // Batch action: Accept all drafts in one request
  const handleAcceptAll = async (msgId) => {
    const message = messages.find((m) => m.id === msgId)
    if (!message) return
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, draft: [], showDraftLoading: true } : m)))
    const acceptedDrafts = message.draft.map((draftItem) => ({
      ...draftItem,
      Status: 'Accepted'
    }))
    try {
      const payload = { query: '', draft: acceptedDrafts, language: language }
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload)
      const result = response
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, showDraftLoading: false } : m)))
      const botMessage = {
        id: result.id,
        role: 'ai',
        content: result.output,
        draft: Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
      }
      setMessages((prev) => [...prev, botMessage])
      // clear selection for this message
      setSelectedDrafts((prev) => {
        const newState = { ...prev }
        delete newState[msgId]
        return newState
      })
    } catch (error) {
      console.error('Error accepting all drafts:', error)
    }
  }

  // Batch action: Accept selected drafts as accepted and unselected as rejected
  const handleAcceptSelected = async (msgId) => {
    const message = messages.find((m) => m.id === msgId)
    if (!message) return
    const selections = selectedDrafts[msgId] || []
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, showDraftLoading: true } : m)))
    // For each draft, if selected then accepted, otherwise rejected.
    const processedDrafts = message.draft.map((draftItem, index) => {
      const status = selections[index] ? 'Accepted' : 'Rejected'
      return { ...draftItem, Status: status }
    })
    try {
      const payload = { query: '', draft: processedDrafts, language: language }
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload)
      const result = response
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, draft: [], showDraftLoading: false } : m)))
      const botMessage = {
        id: result.id,
        role: 'ai',
        content: result.output,
        draft: Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
      }
      setMessages((prev) => [...prev, botMessage])
      setSelectedDrafts((prev) => {
        const newState = { ...prev }
        delete newState[msgId]
        return newState
      })
    } catch (error) {
      console.error('Error accepting selected drafts:', error)
    }
  }

  // Batch action: Reject all drafts in one request
  const handleRejectAll = async (msgId) => {
    const message = messages.find((m) => m.id === msgId)
    if (!message) return
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, draft: [], showDraftLoading: true } : m)))
    const rejectedDrafts = message.draft.map((draftItem) => ({
      ...draftItem,
      Status: 'Rejected'
    }))
    try {
      const payload = { query: '', draft: rejectedDrafts }
      const response = await window.PDR.api.post(`${baseUrl}/conversation`, payload)
      const result = response
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, showDraftLoading: false } : m)))
      const botMessage = {
        id: result.id,
        role: 'ai',
        content: result.output,
        draft: Array.isArray(result.draft) ? result.draft : result.draft ? [result.draft] : []
      }
      setMessages((prev) => [...prev, botMessage])
      setSelectedDrafts((prev) => {
        const newState = { ...prev }
        delete newState[msgId]
        return newState
      })
    } catch (error) {
      console.error('Error rejecting all drafts:', error)
    }
  }

  // Clear the chat and send DELETE /conversation
  const handleClearChat = async () => {
    try {
      await window.PDR.api.delete(`${baseUrl}/conversation`)
      setMessages([])
      setUpdateMessage(null)
      setUpdateHistory([])
      setResponseIntentions([]) // also clear any pending backend intention proposals
      // Also clear any file status / selection upon successful delete per request
      setSelectedFile(null)
      setFileStatus('')
      setFileStatusIsError(false)
      setFileStatusIsSuccess(false)
    } catch (error) {
      console.error('Error clearing chat:', error)
    }
  }

  return (
    <div className="ac-chat">
      <div className="ac-header">
        <h2>Stratsys Copilot</h2>
        <div className="ac-header-actions">
          {/* Streaming toggle */}
          <label className="ac-stream-toggle" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={streamingEnabled} onChange={(e) => setStreamingEnabled(e.target.checked)} />
            Stream
          </label>
          <button className="ac-clear-chat-btn" onClick={handleClearChat}>
            Clear Chat
          </button>
        </div>
      </div>

      {/* Product selector sits outside the main chat window */}
      <div className="ac-product-selector">
        <label htmlFor="product-select">Product:&nbsp;</label>
        <select id="product-select" value={inputProduct} onChange={(e) => setInputProduct(e.target.value)}>
          <option value="">--Choose product--</option>
          {products.map((prod) => (
            <option key={prod} value={prod}>
              {prod}
            </option>
          ))}
        </select>
      </div>

      {/* Location & Context editable state fields */}
      <div className="ac-state-fields">
        <div className="ac-field">
          <label htmlFor="ac-location">Location</label>
          <input
            id="ac-location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Enter location string"
          />
        </div>
        <div className="ac-field">
          <label htmlFor="ac-department">Department</label>
          <input
            id="ac-department"
            type="text"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="Enter department (optional)"
          />
        </div>
        <div className={`ac-field ${!contextIsValid ? 'invalid' : ''}`}>
          <label htmlFor="ac-context">Context (JSON)</label>
          <textarea
            id="ac-context"
            value={contextText}
            onChange={(e) => {
              const val = e.target.value
              setContextText(val)
              if (!val.trim()) {
                setContextIsValid(true)
                setContextError('')
                return
              }
              try {
                JSON.parse(val)
                setContextIsValid(true)
                setContextError('')
              } catch (_) {
                setContextIsValid(false)
                setContextError('Invalid JSON')
              }
            }}
            placeholder='{"currently_open": "Ekonomi"}'
          />
          {!contextIsValid && <span className="ac-field-error">{contextError || 'Invalid JSON'}</span>}
        </div>
      </div>

      <div className="ac-chat-container">
        {/* Floating intention glass bubbles (visualization) */}
        {(intentionsLoading || intentionObjects.length > 0 || intentions.length > 0) && (
          <div className={`ac-intention-bubbles-wrapper ${intentionsLoading ? 'loading' : ''}`}>
            {intentionsLoading && intentions.length === 0 && (
              <div className="ac-intention-bubble skeleton" aria-hidden="true">
                <span className="ac-intention-bubble-label">…</span>
              </div>
            )}
            {(intentionObjects.length > 0 ? intentionObjects : intentions).map((item, idx) => {
              const isObject = intentionObjects.length > 0
              const type = isObject ? item.type : item
              const label = isObject ? item.text : item
              const active = clickedIntentions.includes(type)
              return (
                <button
                  type="button"
                  key={type + idx}
                  className={`ac-intention-bubble ${active ? 'active' : ''}`}
                  onClick={() => toggleClickedIntention(type)}
                  title={active ? 'Selected intention' : 'Click to select intention'}>
                  <span className="ac-intention-bubble-label">{label}</span>
                </button>
              )
            })}
          </div>
        )}
        {messages.map((msg, index) => {
          // HIDE placeholder AI bubble while content is still empty (no drafts & not loading)
          if (
            msg.role === 'ai' &&
            (!msg.content || msg.content.trim().length === 0) &&
            !(msg.draft && msg.draft.length > 0) &&
            !msg.showDraftLoading
          ) {
            return null
          }

          // Suppress AI bubble entirely if responseIntentions are active and this bubble would be the one with intentions
          if (msg.role === 'ai' && responseIntentions.length > 0) {
            // We already render intentions cards instead of this output
            return null
          }

          return (
            <div key={msg.id || index} className={`ac-bubble ${msg.role === 'human' ? 'ac-human' : 'ac-ai'}`}>
              <ReactMarkdown className="ac-message">{msg.content}</ReactMarkdown>
              {/* Render draft card only if draft exists and not already handled */}
              {msg.draft &&
                msg.draft.length > 0 &&
                !msg.showDraftLoading &&
                (msg.draft.length === 1 ? (
                  <div className="ac-draft-container">
                    {msg.draft.map((draftItem, dIndex) => (
                      <div key={dIndex} className="ac-draft-box">
                        <ReactMarkdown className="ac-message">{draftItem.doboard_edit_tool}</ReactMarkdown>
                        <div className="ac-draft-actions">
                          <button onClick={() => handleDraftAccept(msg.id, draftItem)}>Accept</button>
                          <button onClick={() => handleDraftReject(msg.id)}>Don't Accept</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ac-draft-container">
                    {msg.draft.map((draftItem, dIndex) => (
                      <div key={dIndex} className="ac-draft-box">
                        <input
                          type="checkbox"
                          checked={(selectedDrafts[msg.id] && selectedDrafts[msg.id][dIndex]) || false}
                          onChange={(e) => handleCheckboxChange(msg.id, dIndex, e.target.checked)}
                        />
                        <ReactMarkdown className="ac-message">{draftItem.doboard_edit_tool}</ReactMarkdown>
                      </div>
                    ))}
                    <div className="ac-draft-batch-actions">
                      <button onClick={() => handleAcceptAll(msg.id)}>Accept all</button>
                      <button onClick={() => handleAcceptSelected(msg.id)}>Accept selected</button>
                      <button onClick={() => handleRejectAll(msg.id)}>Reject all</button>
                    </div>
                  </div>
                ))}
              {/* Show inline loading indicator if draft is being handled */}
              {msg.showDraftLoading && (
                <div className="ac-draft-loading">
                  Bot is typing<span className="ac-loading-dots"></span>
                </div>
              )}
            </div>
          )
        })}

        {/* Response intention cards (full-width below last message) */}
        {responseIntentions.length > 0 && (
          <div className="ac-response-intentions-wrapper">
            <div className={`ac-response-intentions ${responseIntentions.length === 2 ? 'dual' : 'single'}`}>
              {responseIntentions.map((ri, idx) => (
                <button
                  key={`resp-int-${idx}`}
                  type="button"
                  className="ac-response-intention-card"
                  onClick={() => handleSendResponseIntention(ri)}
                  title={ri.question}>
                  <div className="ac-response-intention-text">{ri.question}</div>
                  {ri.plan && ri.plan.length > 0 && (
                    <div className="ac-response-intention-steps" aria-hidden="true">
                      {ri.plan.map((step, sIdx) => (
                        <div key={`ri-step-${idx}-${sIdx}`} className="ac-response-intention-step">
                          {step.text}
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Approval Required Card */}
        {pendingApproval && (
          <div className="ac-approval-card" role="dialog" aria-labelledby="approval-title">
            <div className="ac-approval-header">
              <span className="ac-approval-icon" aria-hidden="true">
                🔔
              </span>
              <h3 id="approval-title" className="ac-approval-title">
                Approval Required
              </h3>
            </div>
            <p className="ac-approval-description">The AI wants to create the following objects:</p>
            <div className="ac-approval-items">
              {pendingApproval.items.map((item) => {
                const decision = pendingApproval.decisions.get(item.id) || { approved: true }
                const isEditing = editingItemId === item.id
                const displayName = decision.editedName || item.name

                return (
                  <div key={item.id} className="ac-approval-item">
                    {isEditing ? (
                      <div className="ac-approval-item-edit">
                        <input
                          type="checkbox"
                          checked={decision.approved}
                          onChange={() => toggleApprovalItem(item.id)}
                          aria-label={`Approve ${item.name}`}
                        />
                        <input
                          type="text"
                          className="ac-approval-item-input"
                          value={editingItemName}
                          onChange={(e) => setEditingItemName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') confirmEditItem()
                            if (e.key === 'Escape') cancelEditItem()
                          }}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="ac-approval-edit-confirm"
                          onClick={confirmEditItem}
                          aria-label="Confirm edit"
                          title="Confirm">
                          ✓
                        </button>
                        <button
                          type="button"
                          className="ac-approval-edit-cancel"
                          onClick={cancelEditItem}
                          aria-label="Cancel edit"
                          title="Cancel">
                          ✗
                        </button>
                      </div>
                    ) : (
                      <div className="ac-approval-item-view">
                        <div className="ac-approval-item-header">
                          <input
                            type="checkbox"
                            checked={decision.approved}
                            onChange={() => toggleApprovalItem(item.id)}
                            aria-label={`Approve ${displayName}`}
                          />
                          <span className={`ac-approval-item-name ${!decision.approved ? 'rejected' : ''}`}>
                            {displayName}
                            {decision.editedName && <span className="ac-approval-edited-badge">(edited)</span>}
                          </span>
                          <button
                            type="button"
                            className="ac-approval-edit-btn"
                            onClick={() => startEditingItem(item)}
                            aria-label="Edit name"
                            title="Edit name">
                            ✏️
                          </button>
                        </div>
                        <div className="ac-approval-item-meta">
                          {item.parentName && (
                            <one-ux-dropdown class="ac-approval-parent-dropdown" disabled placeholder={item.parentName}>
                              <one-ux-list
                                options={[{ value: String(item.parentContainerId), text: item.parentName }]}
                                value={String(item.parentContainerId)}
                              />
                            </one-ux-dropdown>
                          )}
                          {item.columnName && <one-ux-badge class="ac-approval-column-badge" label={item.columnName} />}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="ac-approval-actions">
              <button
                type="button"
                className="ac-approval-cancel-btn"
                onClick={handleCancelAllApprovals}
                disabled={approvalSubmitting}>
                Cancel All
              </button>
              <button
                type="button"
                className="ac-approval-confirm-btn"
                onClick={handleConfirmSelectedApprovals}
                disabled={approvalSubmitting}>
                {approvalSubmitting ? 'Processing...' : 'Confirm Selected'}
              </button>
            </div>
          </div>
        )}

        {/* Live update bubble (ephemeral) while streaming.
            Click/expand to show ALL previous update_message entries for the current stream. */}
        {loading && streamingEnabled && updateMessage && (
          <div className="ac-bubble ac-update" aria-live="polite" aria-atomic="true">
            <div className="ac-live-indicator">
              <span className="ac-ping"></span>
              <span className="ac-dot"></span>
              <span className="ac-live-label">Live update</span>
            </div>
            <details>
              <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
                <div className="ac-message">
                  <div className="ac-update-card">
                    {updateMessage.icon && typeof updateMessage.icon === 'object' && updateMessage.icon.name ? (
                      <one-ux-icon
                        set={updateMessage.icon.set || undefined}
                        icon={updateMessage.icon.name}
                        class="ac-update-icon"
                      />
                    ) : updateMessage.icon && typeof updateMessage.icon === 'string' ? (
                      <one-ux-icon icon={updateMessage.icon} class="ac-update-icon" />
                    ) : null}
                    <div className="ac-update-text">
                      <ReactMarkdown>{updateMessage.text}</ReactMarkdown>
                    </div>
                    {updateMessage.badge && (
                      <one-ux-badge
                        set="colors"
                        badge={updateMessage.badge.color || undefined}
                        label={updateMessage.badge.text || updateMessage.badge.type || updateMessage.text}
                        class="ac-update-badge">
                        {updateMessage.badge.icon && updateMessage.badge.icon.name ? (
                          <one-ux-icon
                            set={updateMessage.badge.icon.set || undefined}
                            icon={updateMessage.badge.icon.name}
                            slot="start"></one-ux-icon>
                        ) : updateMessage.icon && typeof updateMessage.icon === 'object' && updateMessage.icon.name ? (
                          <one-ux-icon
                            set={updateMessage.icon.set || undefined}
                            icon={updateMessage.icon.name}
                            slot="start"></one-ux-icon>
                        ) : updateMessage.icon && typeof updateMessage.icon === 'string' ? (
                          <one-ux-icon icon={updateMessage.icon} slot="start"></one-ux-icon>
                        ) : null}
                      </one-ux-badge>
                    )}
                  </div>
                </div>
              </summary>
              <div style={{ marginTop: 8 }}>
                {updateHistory.length > 1 ? (
                  <div className="ac-message">
                    {updateHistory.slice(0, -1).map((u, i) => (
                      <div key={`${i}-${(u.text || '').slice(0, 12)}`} style={{ opacity: 0.9, marginTop: 6 }}>
                        <div className="ac-update-card ac-update-card-history">
                          {u.icon && typeof u.icon === 'object' && u.icon.name ? (
                            <one-ux-icon set={u.icon.set || undefined} icon={u.icon.name} class="ac-update-icon" />
                          ) : u.icon && typeof u.icon === 'string' ? (
                            <one-ux-icon icon={u.icon} class="ac-update-icon" />
                          ) : null}
                          <div className="ac-update-text">
                            <ReactMarkdown>{u.text}</ReactMarkdown>
                          </div>
                          {u.badge && (
                            <one-ux-badge
                              set="colors"
                              badge={u.badge.color || undefined}
                              label={u.badge.text || u.badge.type || u.text}
                              class="ac-update-badge">
                              {u.badge.icon && u.badge.icon.name ? (
                                <one-ux-icon
                                  set={u.badge.icon.set || undefined}
                                  icon={u.badge.icon.name}
                                  slot="start"></one-ux-icon>
                              ) : u.icon && typeof u.icon === 'object' && u.icon.name ? (
                                <one-ux-icon
                                  set={u.icon.set || undefined}
                                  icon={u.icon.name}
                                  slot="start"></one-ux-icon>
                              ) : u.icon && typeof u.icon === 'string' ? (
                                <one-ux-icon icon={u.icon} slot="start"></one-ux-icon>
                              ) : null}
                            </one-ux-badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ac-message" style={{ opacity: 0.75, fontStyle: 'italic' }}>
                    No previous updates yet.
                  </div>
                )}
              </div>
            </details>
          </div>
        )}

        {/* For non-streaming, show a generic typing bubble while waiting */}
        {loading && !streamingEnabled && (
          <div className="ac-bubble ac-ai ac-loading">
            <div className="ac-message">
              Bot is typing<span className="ac-loading-dots"></span>
            </div>
          </div>
        )}
      </div>

      <div className="ac-input-area">
        <div className="ac-file-upload compact">
          <label className="ac-file-label">
            <span>Upload</span>
            <input
              type="file"
              accept=".txt,.pdf,.docx,.md,.pptx,.ppt"
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </label>
          <button
            type="button"
            className="ac-upload-btn"
            onClick={handleFileUpload}
            disabled={uploading || !selectedFile}>
            {uploading ? '...' : 'Go'}
          </button>
          {fileStatus && (
            <span
              className="ac-file-status"
              data-error={fileStatusIsError ? '1' : '0'}
              data-success={fileStatusIsSuccess ? '1' : '0'}>
              {fileStatus}
            </span>
          )}
        </div>
        <textarea
          placeholder="Write your question here..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows="3"
        />
        <div className="ac-input-actions">
          <button onClick={handleAsk} disabled={loading}>
            Ask
          </button>
          {loading && streamingEnabled && (
            <button onClick={handleCancelStream} className="ac-cancel-stream-btn">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AnalyzeChat
